import { Component, inject } from '@angular/core';
import { ButtonsModule, DialogModule, DialogService, FormFieldModule, IconModule, PanelModule, SnackBarService } from '@mastechnicaegroup/carelinelive-ui';
import { ErrorPanelComponent } from '@lib/errors/error-panel/error-panel.component';
import { ReactiveFormsModule } from '@angular/forms';
import { RegionService } from '@lib/region/region.service';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { createZoneFormGroup, StoreZoneRequest, ZoneSingleResponse } from '@lib/api/models/zone';
import { Zone } from '@lib/api/models/zone';
import { SendableRequest } from '@mastechnicaegroup/carelinelive-cdk/requests';

export interface RegionData {
    id: string;
    name: string;
}

export interface Data {
    region: RegionData;
    zone: Zone;
}

@Component({
    selector: 'app-edit-region-zone-dialog',
    imports: [
        ButtonsModule,
        DialogModule,
        ErrorPanelComponent,
        FormFieldModule,
        ReactiveFormsModule,
        IconModule,
        PanelModule
    ],
    templateUrl: './edit-region-zone-dialog.component.html',
    standalone: true,
    styleUrl: './edit-region-zone-dialog.component.scss'
})

export class EditRegionZoneDialogComponent {

    private readonly _regionService = inject(RegionService);
    private readonly _snackBar = inject(SnackBarService);

    protected readonly data = inject<Data>(DIALOG_DATA);
    protected readonly dialogRef = inject<DialogRef>(DialogRef);

    protected readonly zoneFormGroup = createZoneFormGroup(this.data.zone);

    editZoneRequest = new SendableRequest<ZoneSingleResponse, StoreZoneRequest>({
        request: payload => this._regionService.updateZone(this.data.zone.id, payload!),

        onSuccess: () => {
            this._snackBar.open({
                colour: 'success',
                dismissable: false,
                icon: 'check_circle',
                title: 'Zone edited successfully',
                actions: [
                    { id: 'undo', label: 'OK' }
                ]
            });

            this.dialogRef.close();
        }
    });

    edit() {

        if (this.zoneFormGroup.invalid) {
            this._snackBar.open({
                colour: 'error',
                dismissable: false,
                icon: 'error_outline',
                title: 'Fill in all required fields',
                actions: [
                    { id: 'undo', label: 'OK' }
                ]
            });
            return;
        }

        const zoneData = {
            'id': this.data.zone.id,
            'name': this.zoneFormGroup.value.name ?? '',
            'description': this.zoneFormGroup.value.description ?? '',
            'status': this.data.zone.status ?? true
        };

        this.editZoneRequest.send(zoneData);
    }

    public static show(dialog: DialogService, data: Data) {
        return dialog.openDialog<DialogRef, Data>(EditRegionZoneDialogComponent, {
            data
        });
    }

}
